import { FormRootConfig } from "types/form";

// ===== Основная информация по ордеру =====
export type OrderStatus = {
  title?: string;
  code?: string;
  color?: string;
};

export type Order = {
  orderNumber?: string;
  orderStatus?: OrderStatus;
};

export const BASE_URL = window.FZ_API_BASE_URL;

export async function fetchOrder(
  orderId: string,
  accessToken: string
): Promise<Order> {
  const url = `${BASE_URL}/front-agent/order/${encodeURIComponent(orderId)}`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: { error_code: number; order: Order } = await r.json();
  if (data.error_code !== 0) throw new Error('Ошибка API');

  return data.order;
}

// ===== Модель по ордеру =====
export type Status = "REJECTED" | "ISSUED" | "IN_PROGRESS";

export const SIDE_MAP = {
  AGENT: "У агента",
  BANK: "В банке",
  CLIENT: "У клиента",
  OTHER: "Другое"
} as const;
type Side = keyof typeof SIDE_MAP;

export type BgCart = {
  code: string;
  name: string;
};

export type BgBankOrder = {
  orderId: string;
  externalOrderNumber: string | null;
  bankName: string;
  bankInn: string;
  cart: string;
  status: Status;
  side: Side;
  applicationSigned: boolean;
  applicationApproved: boolean;
  taskDescription: string | null;
  taskId: string | null;
  externalTaskUrl: string | null;
};

export type CommentsComponentModel = {
  author: string;
  createdAt: string;
  task: string;
  decision: string;
  message: string;
};

export type CommissionComponentModel = {
  attributes: {
    bankMinCommission: number | null;
    bankCommission: number | null;
  };
};

export type CustomerCardComponentModel = {
  customerId?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  displayName?: string;
  shortName?: string;
  fullName?: string;
};

export type ProductBgCardComponentModel = {
  id?: string;
  purchaseNumber?: string;
  guaranteeType?: string;
};

export type BankTaskComponentModel = {
  attributes?: {
    integrationStatusDate?: string;  // ISO-дата в формате 2025-08-27T11:57:05.041Z
    bankStatusDescription?: string; // описание статуса
    bankStatus?: string;            // статус от банка
    bankOrderId?: string;           // идентификатор заявки в банке
    bankOrderNumber?: string;       // номер заявки в банке
    bankTaskName?: string;          // имя задачи
  };
};

export type SignPageComponentModel = {
  attributes?: {
    externalTaskUrl?: string; // ссылка на внешнюю страницу подписи
  };
};

export type FileItem = { 
  fileId: string; 
  fileName: string; 
  size: number;
};

export type DocInfo = {
  docType: string;
  files: FileItem[];
};

export type ModelComponents = {
  bgBankOrders?: {
    carts: BgCart[];
    orders: BgBankOrder[];
  };
  productBgCard?: ProductBgCardComponentModel;
  comments?: CommentsComponentModel[];
  commission?: CommissionComponentModel;
  principal?: CustomerCardComponentModel;
  bankTask?: BankTaskComponentModel;
  signPage?: SignPageComponentModel;
  orderDocuments?: DocInfo[];
};

export type OrderModelResponse = {
  components: ModelComponents;
};

export async function fetchOrderModel(
  orderId: string,
  accessToken: string
): Promise<OrderModelResponse> {
  const url = `${BASE_URL}/front-agent/card/order/${encodeURIComponent(orderId)}/model`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: OrderModelResponse = await r.json();
  return data;
}

export async function fetchOrderConfig(
  orderId: string,
  accessToken: string
): Promise<FormRootConfig> {
  const url = `${BASE_URL}/front-agent/card/order/${encodeURIComponent(orderId)}/config`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: FormRootConfig = await r.json();
  return data;
}

export async function fetchOrderModelByComponent(
  orderId: string,
  component: string,
  accessToken: string
): Promise<OrderModelResponse> {
  const url = `${BASE_URL}/front-agent/card/order/${encodeURIComponent(orderId)}/model?component=${component}`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: OrderModelResponse = await r.json();
  return data;
}

// ===== Задачи по ордеру =====

export async function fetchOrderTaskId(
  orderId: string,
  accessToken: string
): Promise<string | null> {
  const url = `${BASE_URL}/front-agent/order/list?orderIds=${encodeURIComponent(orderId)}`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data = await r.json();
  return data?.order?.[0]?.tasks?.[0]?.task_id ?? null;
}

export type TaskRoute = {
  key: string;
  value: string;
};

export async function fetchOrderTaskRoutes(
  taskId: string,
  accessToken: string
): Promise<TaskRoute[]> {
  const url = `${BASE_URL}/front-agent/task/${encodeURIComponent(taskId)}/config`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data = await r.json();
  return data?.tabs?.task?.components?.bankRoute?.attributes?.bankRoute?.values ?? [];
}

export async function fetchAndAssignOrderTask(
  taskId: string,
  accessToken: string
): Promise<any> {
  const url = `${BASE_URL}/front-agent/task/${encodeURIComponent(taskId)}`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data = await r.json();
  return data;
}

export async function completeTask(
  taskId: string,
  decisionCode: string,
  comment: string,
  bankRoute: string,
  docs?: DocInfo[],
  token?: string
): Promise<void> {
  if (!token) throw new Error('Нет accessToken');

  const payload = {
    type: "COMPLETE",
    taskModel: {
      decision: {
        id: decisionCode,
        comment: comment
      },
      components: {
        decision: {
          id: decisionCode,
          comment: comment
        },
        bankRoute: {
          attributes: {
            bankRoute: bankRoute
          }
        },
        docs: docs
      }
    }
  };

  const resp = await fetch(`${BASE_URL}/front-agent/task/${encodeURIComponent(taskId)}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload ?? {})
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Ошибка завершения задачи: ${resp.status} ${text}`);
  }
}

/* ------------------------------------------------------------------------------------------------
 * DTO под бэкенд (BgAgentMasterStartForm) — типизация 1:1 с Java классами
 * Примечания:
 *  - LocalDate → string ('YYYY-MM-DD')
 *  - BigDecimal → number | string (оставляем гибкость сериализации)
 *  - поля делаем optional, чтобы фронт мог отправлять только заполненные
 * ------------------------------------------------------------------------------------------------ */

export type Company = {
  /** ИНН */
  inn?: string;
};

export type Guarantee = {
  /** Вид банковской гарантии */
  type?: string; // participation | execution | returnAdvance | period ...
  /** Дата начала гарантии (YYYY-MM-DD) */
  startDate?: string;
  /** Дата окончания гарантии (YYYY-MM-DD) */
  endDate?: string;
  /** Срок действия гарантии, дней */
  duration?: number | null;
  /** Итоговая сумма гарантии, руб. */
  amount?: number | string | null;
};

export type PurchaseLotProduct = {
  /** Код по ОКПД */
  okpdCode?: string;
  /** Название по ОКПД */
  okpdName?: string;
};

export type PurchaseLotContractConditions = {
  /** Предусмотрен аванс */
  prepaymentExists?: boolean | null;
  /** Сумма аванса */
  prepaymentAmount?: number | string | null;
  /** Бесспорное списание */
  requiredIndisputableDebiting?: boolean | null;
  /** Неустойки */
  includedForfeit?: boolean | null;
  /** Требование об обеспечении гарантийного периода */
  requiredSecurityForGuaranteePeriod?: boolean | null;
  /** Срок гарантийного периода в днях */
  securityForGuaranteePeriodDays?: string | null;
  /** Сумма гарантийного обслуживания */
  securityForGuaranteePeriodAmount?: number | string | null;
};

export type PurchaseLotBeneficiaryContract = {
  /** Контракт заключен */
  concluded?: boolean | null;
  /** № контракта */
  contractNumber?: string | null;
  /** Дата контракта (YYYY-MM-DD) */
  contractDate?: string | null;
  /** Реестровый № */
  regNumber?: string | null;
  /** Сумма контракта */
  contractAmount?: number | string | null;
  /** Ссылка на контракт */
  contractLink?: string | null;
};

export type PurchaseLotBeneficiary = {
  /** ИНН */
  inn?: string;
  /** КПП */
  kpp?: string | null;
  /** ОГРН */
  ogrn?: string | null;
  /** Наименование компании */
  displayName?: string;
  /** Юридический адрес бенефициара */
  legalAddress?: string | null;
  /** Фактический адрес бенефициара */
  actualAddress?: string | null;
  /** Телефон */
  phone?: string | null;
  /** Email */
  email?: string | null;
  /** НМЦК по бенефициару */
  purchaseAmount?: number | string | null;
  /** Регномер заказчика на zakupki.gov.ru */
  customerRegNum?: string | null;
  /** Место доставки */
  deliveryPlace?: string | null;
  /** Размер обеспечения заявки (на участие) */
  appAmount?: number | string | null;
  /** Размер обеспечения контракта (на исполнение) */
  conAmount?: number | string | null;
  /** Сумма гарантии */
  bgAmount?: number | string | null;
  /** Информация о контракте */
  contract?: PurchaseLotBeneficiaryContract | null;
};

export type PurchaseLot = {
  /** Номер лота */
  lotNumber?: number | null;
  /** Предмет контракта */
  lotSubject?: string | null;
  /** НМЦК */
  startAmount?: number | string | null;
  /** Предложенная цена */
  principalOfferAmount?: number | string | null;
  /** ИКЗ */
  ikzList?: string[] | null;
  /** Товары */
  products?: PurchaseLotProduct[] | null;
  /** Условия контракта */
  contractConditions?: PurchaseLotContractConditions | null;
  /** Бенефициары */
  beneficiaries?: PurchaseLotBeneficiary[] | null;
};

export type Purchase = {
  /** РНТ (Реестровый № торгов) */
  purchaseNumber?: string;
  /** Торговая площадка */
  marketPlace?: string | null;
  /** Тип аукциона */
  auctionType?: string | null;
  /** Способ размещения */
  tenderType?: string | null;
  /** Закон (44-ФЗ/223-ФЗ/185-ФЗ/Коммерческая закупка) */
  law?: string;
  /** Дата размещения тендера (YYYY-MM-DD) */
  tenderDate?: string | null;
  /** Ссылка на тендер */
  purchaseLink?: string | null;
  /** Предмет закупки */
  purchaseSubject?: string | null;
  /** Лоты */
  lots?: PurchaseLot[] | null;
};

export type BgAgentMasterStartForm = {
  principal?: Company | null;
  guarantee?: Guarantee | null;
  purchase?: Purchase | null;
  requestedBanks?: string[];
};

// ===== Старт процесса — возвращает orderId (строку) =====
/**
 * Стартует процесс в бэкенде.
 * @param processCode код процесса
 * @param form объект данных для старта процесса (BgAgentMasterStartForm)
 * @param token accessToken для авторизации
 * @returns строка orderId
 */
export async function startProcess(
  processCode: string,
  form: BgAgentMasterStartForm,
  token?: string
): Promise<string> {
  if (!token) throw new Error('Нет accessToken');

  const resp = await fetch(`${BASE_URL}/front-agent/process/start/${encodeURIComponent(processCode)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(form ?? {})
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Ошибка старта процесса: ${resp.status} ${text}`);
  }
  const orderId = (await resp.text()).trim();
  return orderId;
}

// ===== Запрос статуса =====
export async function requestOrderStatus(
  orderId: string,
  token?: string
): Promise<void> {
  if (!token) throw new Error('Нет accessToken');

  const resp = await fetch(`${BASE_URL}/front-bg/bg/${encodeURIComponent(orderId)}/status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Ошибка старта процесса: ${resp.status} ${text}`);
  }
}

// ===== Запрос помощи консультанта =====
export async function requestHelpByOrder(
  orderId: string,
  token?: string
): Promise<void> {
  if (!token) throw new Error('Нет accessToken');

  const resp = await fetch(`${BASE_URL}/front-bg/bg/${encodeURIComponent(orderId)}/help`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Ошибка старта процесса: ${resp.status} ${text}`);
  }
}
